import * as React from 'react';

export type NotificationSeverity = 'success' | 'failure' | 'warning' | 'info';

export type Notification = {
    id: number;
    severity: NotificationSeverity;
    text: string | React.ReactNode;
};

type Listener = (notifications: Notification[]) => void;

let notifications: Notification[] = [];
let listeners: Listener[] = [];
let nextId = 1;

const fallbackFailureText = 'Something went wrong';

function displayableText(value: unknown): string | React.ReactNode {
    if (typeof value === 'string' || React.isValidElement(value)) {
        return value;
    }
    if (value instanceof Error) {
        return value.message || fallbackFailureText;
    }
    if (value && typeof value === 'object') {
        const messages = Object.keys(value)
            .map((key) => value[key])
            .filter((entry) => typeof entry === 'string' && entry !== '');
        if (messages.length > 0) {
            return messages.join(' ');
        }
    }
    return fallbackFailureText;
}

function publish(severity: NotificationSeverity, text: unknown) {
    notifications = [...notifications, { id: nextId, severity, text: displayableText(text) }];
    nextId += 1;
    listeners.forEach((listener) => listener(notifications));
}

export function currentNotifications(): Notification[] {
    return notifications;
}

export function dismissNotification(id: number) {
    notifications = notifications.filter((notification) => notification.id !== id);
    listeners.forEach((listener) => listener(notifications));
}

export function subscribeToNotifications(listener: Listener): () => void {
    listeners = [...listeners, listener];
    return () => {
        listeners = listeners.filter((subscribed) => subscribed !== listener);
    };
}

const notify = {
    success(text: string | React.ReactNode) {
        publish('success', text);
    },
    failure(text: unknown) {
        publish('failure', text);
    },
    warning(text: string | React.ReactNode) {
        publish('warning', text);
    },
    info(text: string | React.ReactNode) {
        publish('info', text);
    }
};

export default notify;
